import { toast } from "react-toastify";
import { LambdaDependencyOptions, LambdaRunBatchView, LambdaRunOptions, LambdaRunsResult, LambdaScreenDependenciesResult, LambdaTrackerDetail, LambdaTrackerFields, LambdaTrackerItem, LambdaTrackerResult } from "models/lambda-tracker";

const postJson = async (url: string, body: unknown) => {
    const res = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(body ?? {}),
        headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message);
    return data;
}

/** Recent manual runs (Run / Update all), newest first. */
export const getLambdaRuns = async (): Promise<LambdaRunsResult | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker/runs`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as LambdaRunsResult;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting the manual runs");
        return null;
    }
}

/** Queues a manual run of one Lambda. */
export const runLambda = async (id: string, options: LambdaRunOptions): Promise<LambdaRunBatchView | null> => {
    try {
        const data = await postJson(`${process.env.REACT_APP_API_URL}/lambda-tracker/${id}/run`, options);
        toast.success(data.message);
        return data.data as LambdaRunBatchView;
    } catch (err: any) {
        toast.error(err.message ?? "Error starting the run");
        return null;
    }
}

/** Queues "Update all": every active Lambda, source syncs first, then the reports. */
export const runAllLambdas = async (options: LambdaRunOptions): Promise<LambdaRunBatchView | null> => {
    try {
        const data = await postJson(`${process.env.REACT_APP_API_URL}/lambda-tracker/runs/all`, options);
        toast.success(data.message);
        return data.data as LambdaRunBatchView;
    } catch (err: any) {
        toast.error(err.message ?? "Error starting Update all");
        return null;
    }
}

/** Cancels the runs of a batch that have not started yet. */
export const cancelLambdaRun = async (batchId: string): Promise<LambdaRunBatchView | null> => {
    try {
        const data = await postJson(`${process.env.REACT_APP_API_URL}/lambda-tracker/runs/${batchId}/cancel`, {});
        toast.success(data.message);
        return data.data as LambdaRunBatchView;
    } catch (err: any) {
        toast.error(err.message ?? "Error cancelling the run");
        return null;
    }
}

/** Every tracked Lambda with its run timeline for the last `days` days. */
export const getLambdaTracker = async (days: number): Promise<LambdaTrackerResult | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker?days=${days}`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as LambdaTrackerResult;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting the Lambda tracker");
        return null;
    }
}

/** Screens with the Lambdas their data depends on, for the last `days` days. */
export const getLambdaScreenDependencies = async (days: number): Promise<LambdaScreenDependenciesResult | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker/dependencies?days=${days}`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as LambdaScreenDependenciesResult;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting the screen dependencies");
        return null;
    }
}

/** Screens and Lambdas the dependency editor can choose from. */
export const getLambdaDependencyOptions = async (): Promise<LambdaDependencyOptions | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker/dependencies/options`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as LambdaDependencyOptions;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting the screens and Lambdas");
        return null;
    }
}

/** Replaces the Lambdas a screen depends on, in run order. */
export const saveScreenDependencies = async (screenCode: string, lambdaIds: string[]): Promise<boolean> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker/dependencies/${encodeURIComponent(screenCode)}`, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify({ lambdaIds }),
            headers: { 'Content-Type': 'application/json' },
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Screen dependencies saved.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error saving the screen dependencies");
        return false;
    }
}

/** One Lambda with every run and its stats for the last `days` days. */
export const getLambdaTrackerItem = async (id: string, days: number): Promise<LambdaTrackerDetail | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker/${id}?days=${days}`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as LambdaTrackerDetail;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting the Lambda");
        return null;
    }
}

/** Saves the display info and expected schedule. Returns the updated settings. */
export const submitUpdateLambdaTracker = async (
    id: string,
    vals: LambdaTrackerFields
): Promise<Partial<LambdaTrackerItem> | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lambda-tracker/${id}`, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify({
                displayName: vals.displayName,
                description: vals.description,
                lambdaFunctionName: vals.lambdaFunctionName,
                scheduleCron: vals.scheduleCron,
                graceMinutes: Number(vals.graceMinutes),
                isActive: vals.isActive,
                syncButtonScreens: vals.syncButtonScreens,
                rdsTables: vals.rdsTables,
                acumaticaGIs: vals.acumaticaGIs,
                endpointUrl: vals.endpointUrl,
                notes: vals.notes,
            }),
            headers: { 'Content-Type': 'application/json' },
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Lambda updated.");
        const data = await res.json();
        return data.data as Partial<LambdaTrackerItem>;
    } catch (err: any) {
        toast.error(err.message ?? "Error updating the Lambda");
        return null;
    }
}
